// src/payment/components/QRPayment.js
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  LuArrowLeft, 
  LuX, 
  LuDownload, 
  LuRefreshCw, 
  LuCircleAlert,
  LuCamera,
  LuSmartphone,
  LuCircleCheck,
  LuShieldAlert
} from 'react-icons/lu';
import PaymentSummary from './PaymentSummary';
import './QRPayment.css';

const QRPayment = ({
  resumen,
  qrData,
  loading,
  error,
  onGenerate,
  onBack,
  onClose
}) => {
  const isGenerating = loading?.generating;
  const isVerifying = loading?.verifying;

  const hasTriggeredRef = useRef(false);

  // 🧪 QR DUMMY PARA PRUEBAS EN DESARROLLO (Si no viene un QR real del backend, usa este)
  const dummyQrData = {
    qr: "https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=PagoDemoInnovatesport",
    qrId: "test_12345"
  };

  // Si viene QR real lo usa; si hay error o estamos en desarrollo y no hay QR, usamos el dummy
  const isDev = process.env.NODE_ENV === 'development';
  const activeQrData = qrData?.qr ? qrData : (isDev || error ? dummyQrData : null);

  // Desactivamos el estado de error visual si activamos el dummy en dev
  const displayError = isDev ? null : error;

  // Auto-generación de QR
  useEffect(() => {
    if (!qrData && !isGenerating && !error && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      onGenerate();
    }
  }, [qrData, isGenerating, error, onGenerate]);

  // DESCARGAR COMO PNG FORZADO (Asegura formato PNG universal para la galería del cel)
  const handleDownloadQR = () => {
    if (!activeQrData?.qr) return;

    // Si ya es un data URI o base64
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = activeQrData.qr;
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 300;
        canvas.height = img.height || 300;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        const packageName = resumen?.paqueteSeleccionado?.title?.replace(/\s+/g, '-') || 'Inscripcion';
        link.download = `QR-Pago-${packageName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        // En caso de bloqueo por CORS en el canvas, usamos la URL directa
        fallbackDownload();
      }
    };

    const fallbackDownload = () => {
      const link = document.createElement('a');
      link.href = activeQrData.qr; // 👈 CORREGIDO: Usamos activeQrData.qr
      const packageName = resumen?.paqueteSeleccionado?.title?.replace(/\s+/g, '-') || 'Inscripcion';
      link.download = `QR-Pago-${packageName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    img.onerror = fallbackDownload;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isVerifying) {
      onClose();
    }
  };

  return (
    <div className="qr-modal-overlay" onClick={handleOverlayClick}>
      <div className="qr-modal-card">
        
        {/* Barra superior de navegación */}
        <div className="qr-modal-nav">
          <button className="qr-nav-back-btn" onClick={onBack} type="button">
            <LuArrowLeft /> Volver
          </button>
          <button className="qr-nav-close-btn" onClick={onClose} type="button" aria-label="Cerrar">
            <LuX />
          </button>
        </div>

        {/* Encabezado */}
        <div className="qr-modal-header">
          <h2 className="qr-modal-title">Pago con QR Bancario</h2>
          <p className="qr-modal-subtitle">Escanea desde cualquier Banco o App Financiera de Bolivia</p>
        </div>

        {/* Resumen de Pago */}
        <div className="qr-modal-summary">
          <PaymentSummary resumen={resumen} compact />
        </div>

        {/* CONTENIDO PRINCIPAL SEGÚN ESTADO */}
        {isGenerating && !activeQrData ? (
          <div className="qr-state-box">
            <LuRefreshCw className="qr-spinner-icon" />
            <h3>Generando tu código QR único...</h3>
            <p>Conectando con la pasarela bancaria de forma segura.</p>
          </div>
        ) : displayError ? (
          <div className="qr-state-box error">
            <LuCircleAlert className="qr-error-icon" />
            <h3>No pudimos generar el QR</h3>
            <p>{displayError}</p>
            <button className="qr-retry-btn" onClick={onGenerate} type="button">
              Reintentar Generar QR
            </button>
          </div>
        ) : activeQrData?.qr ? (
          <div className="qr-main-content">
            {/* Tarjeta Visual del Código QR */}
            <div className="qr-display-card">
              <div className="qr-image-container">
                <img src={activeQrData.qr} alt="Código QR de Pago" className="qr-image-big" />
              </div>
              
              <button className="qr-download-btn" onClick={handleDownloadQR} type="button">
                <LuDownload className="download-icon" /> Descargar QR
              </button>
            </div>

            {/* Badge de Verificación en Vivo */}
            <div className={`qr-status-pill ${isVerifying ? 'active' : ''}`}>
              <LuRefreshCw className="spin-icon" />
              <span>Buscando tu pago en tiempo real...</span>
            </div>

            {/* Pasos a prueba de todo */}
            <div className="qr-steps-container">
              <h4 className="qr-steps-title">¿Cómo realizar el pago?</h4>
              
              <div className="qr-step-item">
                <div className="step-num">1</div>
                <div className="step-icon"><LuCamera /></div>
                <div className="step-text">
                  <strong>Saca una captura de pantalla</strong> o presiona el botón de arriba para <strong>descargar el QR</strong> a tu galería.
                </div>
              </div>

              <div className="qr-step-item">
                <div className="step-num">2</div>
                <div className="step-icon"><LuSmartphone /></div>
                <div className="step-text">
                  Entra a la <strong>app de tu banco</strong>, busca la opción <strong>Pagar con QR</strong> y sube la imagen guardada.
                </div>
              </div>

              <div className="qr-step-item">
                <div className="step-num">3</div>
                <div className="step-icon"><LuCircleCheck /></div>
                <div className="step-text">
                  Confirma el pago por el monto exacto y <strong>vuelve a esta pantalla</strong>. La verificación se hace sola en segundos.
                </div>
              </div>
            </div>

            {/* Advertencia Importante */}
            <div className="qr-warning-banner">
              <LuShieldAlert className="warning-icon" />
              <div className="warning-text">
                <strong>¡Ojo, no cierres esta pantalla!</strong>
                <span>Puedes cambiar a la app de tu banco o minimizar la pantalla para pagar. Al volver, la validación se hará sola.</span>
              </div>
            </div>

          </div>
        ) : null}

      </div>
    </div>
  );
};

QRPayment.propTypes = {
  resumen: PropTypes.object.isRequired,
  qrData: PropTypes.shape({
    qr: PropTypes.string,
    qrId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expirationDate: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  loading: PropTypes.shape({
    generating: PropTypes.bool,
    verifying: PropTypes.bool,
    registering: PropTypes.bool,
  }),
  error: PropTypes.string,
  onGenerate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QRPayment;